import type { ModelMessage, ToolUIPart } from 'ai'

/**
 * Shared tool UI message plumbing.
 *
 * All tool modules (Excel/Sheets/common) use the same callback so the chat UI can
 * display tool executions consistently without duplicating boilerplate.
 *
 * Tools emit two messages per invocation:
 * 1. "start" (state: input-available) — when the tool begins executing
 * 2. "finish" (state: output-available/error/denied) — when execution completes
 *
 * Both share the same toolCallId so the UI can update the part in-place.
 */

type ToolMessageAction = 'start' | 'update'

export type ToolUIMessage = ModelMessage & {
  /** Whether this is a new tool part or an update to an existing one */
  _action: ToolMessageAction
}

let toolMessageCallback: ((message: ToolUIMessage) => void) | null = null

export function setToolMessageCallback(
  callback: (message: ToolUIMessage) => void
) {
  toolMessageCallback = callback
}

export function clearToolMessageCallback() {
  toolMessageCallback = null
}

function createToolUIMessage(
  action: ToolMessageAction,
  toolCallId: string,
  toolName: string,
  state: ToolUIPart['state'],
  input: unknown,
  output?: unknown,
  errorText?: string
): ToolUIMessage {
  const toolPart: ToolUIPart = {
    type: `tool-${toolName}` as ToolUIPart['type'],
    toolCallId,
    state,
    input,
    output,
    errorText,
  } as ToolUIPart

  return {
    id: crypto.randomUUID(),
    role: 'assistant',
    content: '',
    parts: [toolPart],
    _action: action,
  } as ToolUIMessage
}

/**
 * Emit a "tool started" message (state: input-available).
 * Called via onInputAvailable hook when a tool begins executing.
 */
export function emitToolStartMessage(
  toolCallId: string,
  toolName: string,
  input: unknown
) {
  if (!toolMessageCallback) return

  toolMessageCallback(
    createToolUIMessage('start', toolCallId, toolName, 'input-available', input)
  )
}

/**
 * Emit a "tool finished" message that updates the existing tool part.
 * Called from onStepFinish when a tool completes.
 */
export function emitToolMessage(
  toolCall: {
    toolCallId: string
    toolName: string
    title?: string
    input: unknown
  },
  result?: { output?: unknown },
  error?: unknown
) {
  if (!toolMessageCallback) return

  let parsedError = error
  let output = result?.output
  let hasPartialErrors = false

  // Check if output is an error object (tools return { error: string } on failure)
  if (
    output &&
    typeof output === 'object' &&
    'error' in output &&
    typeof output.error === 'string'
  ) {
    parsedError = output.error
    output = undefined
  }

  // Check for partial errors: success: true with errors array
  if (
    output &&
    typeof output === 'object' &&
    'success' in output &&
    output.success === true &&
    'errors' in output &&
    Array.isArray(output.errors) &&
    output.errors.length > 0
  ) {
    hasPartialErrors = true
  }

  const toolName = toolCall.title ?? toolCall.toolName
  const errorString = parsedError ? String(parsedError) : undefined

  // Determine final state
  let state: ToolUIPart['state'] = 'output-available'
  if (errorString) {
    state = 'output-error'
  } else if (hasPartialErrors) {
    state = 'output-denied'
  }

  toolMessageCallback(
    createToolUIMessage(
      'update',
      toolCall.toolCallId,
      toolName,
      state,
      toolCall.input,
      errorString ? undefined : output,
      errorString
    )
  )
}
